import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { navLinks, siteData } from '../data/siteData'

export default function Navbar() {
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const isHome = location.pathname === '/'

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80)

      // Determine active section
      const sections = navLinks.map(l => l.href.replace('#', ''))
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i])
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= 150) {
            setActiveSection(sections[i])
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-steel/95 backdrop-blur-xl border-b border-military/30 shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              className="flex items-center gap-3 group"
            >
              <img
                src="/images/logo.png"
                alt="Strefa TOP"
                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <span className="font-display text-2xl text-bone tracking-wider">STREFA TOP</span>
                <p className="font-mono text-[10px] text-chrome tracking-[0.3em] uppercase -mt-1">
                  ASG & Paintball
                </p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = isHome && activeSection === link.href.replace('#', '')
                const isRoute = link.href.startsWith('/')
                return (
                  <Link
                    key={link.href}
                    to={isRoute ? link.href : '/' + link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`relative px-4 py-2 text-sm font-subheading font-medium tracking-wide uppercase transition-colors duration-300 ${
                      isActive || (isRoute && location.pathname === link.href)
                        ? 'text-military-light'
                        : 'text-chrome hover:text-bone'
                    }`}
                  >
                    {link.label}
                    {(isActive || (isRoute && location.pathname === link.href)) && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute bottom-0 left-2 right-2 h-0.5 bg-military-light rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                )
              })}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/#contact"
                className="relative px-6 py-2.5 bg-military-light text-bone font-subheading font-bold text-sm uppercase tracking-wider rounded-sm overflow-hidden group transition-all duration-300 hover:bg-military-glow hover:shadow-lg hover:shadow-military-light/20 hover:scale-[1.04]"
              >
                <span className="relative z-10">Zarezerwuj</span>
                <div className="absolute inset-0 bg-gradient-to-r from-military-light to-military-glow opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-bone hover:text-military-light transition-colors"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-steel/95 backdrop-blur-xl"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-steel border-l border-military/20 p-8 pt-24"
            >
              <div className="flex flex-col gap-2">
                {navLinks.map((link, i) => {
                  const isActive = isHome && activeSection === link.href.replace('#', '')
                  const isRoute = link.href.startsWith('/')
                  return (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Link
                        to={isRoute ? link.href : '/' + link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`block text-left px-4 py-3 font-subheading font-semibold text-lg uppercase tracking-wide transition-colors ${
                          isActive || (isRoute && location.pathname === link.href)
                            ? 'text-military-light border-l-2 border-military-light'
                            : 'text-chrome hover:text-bone'
                        }`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  )
                })}
              </div>

              <div className="mt-8 pt-8 border-t border-military/20">
                <Link
                  to="/#contact"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full py-4 text-center bg-military-light text-bone font-subheading font-bold uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors"
                >
                  Zarezerwuj teraz
                </Link>
                <a
                  href={`tel:${siteData.phone}`}
                  className="mt-4 flex items-center justify-center gap-2 text-chrome hover:text-bone transition-colors"
                >
                  <span className="font-mono text-sm">{siteData.phone}</span>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
